'use client'

import { useState } from 'react'
import { useTab } from '@/contexts/TabContext'
import { useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { HamburgerMenu } from './HamburgerMenu'
import { useUnreadCount } from '@/hooks/useDirectMessages'

export function Navigation() {
  const { activeTab, setActiveTab } = useTab()
  const { isAuthenticated, canAccessToolsTab, canAccessOrganizeTab, profile } = useAuth()
  const { t } = useLanguage()
  const [menuOpen, setMenuOpen] = useState(false)

  // Real-time unread count from Socket.io
  const unreadCount = useUnreadCount()

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden md:flex items-center space-x-3 lg:space-x-5 xl:space-x-6 text-xs lg:text-sm">
        {/* Home/Landing */}
        <button
          onClick={() => setActiveTab('landing')}
          className={`whitespace-nowrap ${
            activeTab === 'landing'
              ? 'text-gray-900 font-medium'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t('nav.home')}
        </button>
        {canAccessOrganizeTab && (
          <button
            onClick={() => setActiveTab('home')}
            className={`whitespace-nowrap ${
              activeTab === 'home'
                ? 'text-gray-900 font-medium'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('nav.organize')}
          </button>
        )}
        <button
          onClick={() => setActiveTab('reading')}
          className={`whitespace-nowrap ${
            activeTab === 'reading'
              ? 'text-gray-900 font-medium'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t('nav.reading')}
        </button>
        <button
          onClick={() => setActiveTab('resources')}
          className={`whitespace-nowrap ${
            activeTab === 'resources'
              ? 'text-gray-900 font-medium'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t('nav.resources')}
        </button>
        <button
          onClick={() => setActiveTab('mutualAid')}
          className={`whitespace-nowrap ${
            activeTab === 'mutualAid'
              ? 'text-gray-900 font-medium'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t('nav.mutualAid')}
        </button>
        {canAccessToolsTab && (
          <button
            onClick={() => setActiveTab('tools')}
            className={`whitespace-nowrap ${
              activeTab === 'tools'
                ? 'text-gray-900 font-medium'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('nav.tools')}
          </button>
        )}
        <button
          onClick={() => setActiveTab('profile')}
          className={`whitespace-nowrap flex items-center gap-2 relative ${
            activeTab === 'profile'
              ? 'text-gray-900 font-medium'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {isAuthenticated ? t('nav.profile') : t('nav.login')}
          {isAuthenticated && unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
        <a
          href="https://renosparkstenantsunion.org"
          className="text-rstu-red hover:text-rstu-red-dark font-medium whitespace-nowrap"
        >
          {t('nav.mainSite')}
        </a>
      </nav>

      {/* Mobile Navigation */}
      <div className="md:hidden flex items-center gap-2">
        {/* Login/Profile button */}
        <button
          onClick={() => setActiveTab('profile')}
          className={`text-sm px-2.5 py-1 rounded-md border transition-colors relative ${
            activeTab === 'profile'
              ? 'border-rstu-red bg-rstu-red text-white'
              : 'border-rstu-red/60 text-rstu-red hover:border-rstu-red'
          }`}
        >
          <div className="flex items-center gap-1">
            {isAuthenticated ? t('nav.profile') : t('nav.login')}
            {isAuthenticated && unreadCount > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
        </button>
        {/* Hamburger menu */}
        <button
          onClick={() => setMenuOpen(true)}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
          aria-label="Open menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Mobile Menu Drawer */}
      <HamburgerMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  )
}
